/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { Capacitor } from '@capacitor/core';
import { log } from '../../util/log';

export class AdobeAnalytics {
  private static instance: AdobeAnalytics;

  private readonly defaultData: {
    device: {
      platform: string;
      deviceType?: string;
      deviceId?: string;
    };
    user: {
      userId?: string | null;
      mcid?: string | null;
      userType: 'guest' | 'registered';
      loginStatus: 'logged-in' | 'logged-out';
    };
  };

  private eventQueue: Array<Record<string, any>> = [];
  private isUserSet = false;
  private isATTAccepted = true;
  private constructor() {
    this.defaultData = {
      device: {
        platform: this.getPlatform(),
      },
      user: {
        userId: null,
        mcid: null,
        userType: 'guest',
        loginStatus: 'logged-out',
      },
    };

    // this.isATTAccepted = !(Capacitor.getPlatform() === 'ios')
  }

  public static getInstance(): AdobeAnalytics {
    if (!AdobeAnalytics.instance) {
      AdobeAnalytics.instance = new AdobeAnalytics();
    }
    return AdobeAnalytics.instance;
  }

  private getPlatform(): string {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') {
      return 'app';
    }
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile-web' : 'web';
  }

  public setATTStatus(granted: boolean) {
    this.isATTAccepted = granted;
  }

  private pushToDataLayer(data: Record<string, any>) {
    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(data);
    log('Data Layer updated:', data);
  }

  private processQueue() {
    if (this.isUserSet) {
      log('Processing queued events:', this.eventQueue);
      this.eventQueue.forEach((event) =>
        this.pushToDataLayer({
          ...event,
          ...this.defaultData,
        }),
      );
      this.eventQueue = [];
    }
  }

  public trackEvent(event: string, payload: Record<string, any> = {}) {
    const pageName = location.pathname;
    const pageURL = window.location.href;
    const page = {
      pageName,
      pageURL,
    };
    const eventData = {
      event,
      page: {
        ...page,
        ...payload.page,
      },
      ...this.defaultData,
      ...payload,
    };

    if (this.isUserSet) {
      this.pushToDataLayer(eventData);
    } else {
      log('Event queued until user data is set:', eventData);
      this.eventQueue.push(eventData);
    }
  }

  public setUser(
    userId: string,
    userType: 'guest' | 'registered',
    loginStatus: 'logged-in' | 'logged-out',
  ) {
    this.defaultData.user = { ...this.defaultData.user, userId, userType, loginStatus };
    this.isUserSet = true;
    log('User data set:', this.defaultData.user);
    this.processQueue();
  }

  public setMcid(mcid: string | null) {
    this.defaultData.user = { ...this.defaultData.user, mcid };
  }

  public updateDeviceInfo(deviceType: string, deviceId: string) {
    this.defaultData.device.deviceType = deviceType;
    this.defaultData.device.deviceId = deviceId;
    log('Device data updated:', this.defaultData.device);
  }
}
